import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel, StatCard, PillTabs, Empty, NativeSelect } from "@/components/crm/common";
import { MeetingForm } from "@/components/crm/meetings/MeetingForm";
import { MomDialog } from "@/components/crm/meetings/MomDialog";
import { MeetingCard } from "@/components/crm/meetings/MeetingCard";
import { ActionForm } from "@/components/crm/meetings/ActionForm";
import { ActionsBoard } from "@/components/crm/meetings/ActionsBoard";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const A_FILTERS = { mine: (a) => a.mine, team: (a) => !a.mine, overdue: (a) => a.overdue, open: (a) => a.status !== "Done", done: (a) => a.status === "Done" };

export default function Meetings() {
  const { isAdmin, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "meetings";
  const { items: team } = useCollection("team");
  const { items: projects } = useCollection("projects");
  const [meetings, setMeetings] = useState([]);
  const [acts, setActs] = useState({ items: [] });
  const [mForm, setMForm] = useState(null);
  const [mom, setMom] = useState(null);
  const [aForm, setAForm] = useState(null);
  const [mf, setMf] = useState("upcoming");
  const [af, setAf] = useState("open");
  const [desig, setDesig] = useState("");
  const load = () => { api.get("/meetings").then((r) => setMeetings(r.data)); api.get("/actions").then((r) => setActs(r.data)); };
  useEffect(() => { load(); }, []);
  const cancel = async (m) => {
    if (!window.confirm(`Cancel "${m.title}"?`)) return;
    try { await api.post(`/meetings/${m.id}/cancel`); toast.success("Meeting cancelled"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const runReminders = async () => {
    try { const r = await api.post("/actions/run-reminders"); toast.success(`Reminders sent: ${r.data.due_soon} due soon, ${r.data.overdue} overdue, ${r.data.escalated.length} escalated`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const mRows = meetings.filter((m) => (mf === "upcoming" ? m.status === "Scheduled" && m.date >= today() : mf === "past" ? m.status !== "Scheduled" || m.date < today() : true));
  const items = acts.items.filter((a) => !desig || a.designation === desig);
  const canSchedule = can("Meetings", "create");
  return (
    <>
      <PageHeader num="25" title="Meetings & POA" subtitle="Schedule meetings and invite people by name, publish minutes (MOM), assign action items, and let reminders and escalation to reporting managers run automatically.">
        {isAdmin && <Button data-testid="poa-run-reminders" variant="outline" onClick={runReminders}><BellRing className="mr-1 h-4 w-4" />Run reminders now</Button>}
        <Button data-testid="action-add-button" variant="outline" onClick={() => setAForm({})}><Plus className="mr-1 h-4 w-4" />Action item</Button>
        {canSchedule && <Button data-testid="meeting-add-button" onClick={() => setMForm({ date: today() })}><Plus className="mr-1 h-4 w-4" />Schedule meeting</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard testid="mt-stat-upcoming" label="Upcoming meetings" value={meetings.filter((m) => m.status === "Scheduled" && m.date >= today()).length} />
        <StatCard testid="mt-stat-mine" label="My open actions" value={acts.items.filter((a) => a.mine && a.status !== "Done").length} />
        <StatCard testid="mt-stat-overdue" label="Overdue actions" tone="text-rose-500" value={acts.items.filter((a) => a.overdue).length} />
        <StatCard testid="mt-stat-escalated" label="Escalated" tone="text-rose-500" value={acts.items.filter((a) => a.overdue && a.escalation_level).length} />
      </div>
      <PillTabs testid="meetings-tab" value={tab} onChange={(t) => setParams({ tab: t })} tabs={[["meetings", "Meetings & MOM", meetings.length], ["actions", "Action items (POA)", acts.items.filter((a) => a.status !== "Done").length]]} />
      {tab === "meetings" ? (
        <Panel testid="meetings-panel" title="Meetings" action={<PillTabs testid="meetings-filter" value={mf} onChange={setMf} tabs={[["upcoming", "Upcoming"], ["past", "Past & MOM"], ["all", "All"]]} />}>
          {mRows.length ? <div className="space-y-3">{mRows.map((m) => <MeetingCard key={m.id} m={m} onEdit={setMForm} onMom={setMom} onCancel={cancel} onAction={(x) => setAForm({ meeting_id: x.id })} />)}</div> : <Empty text="No meetings here." />}
        </Panel>
      ) : (
        <Panel testid="actions-panel" title="Action items by designation" action={<div className="flex flex-wrap gap-2">
          <NativeSelect testid="actions-designation-filter" value={desig} onChange={setDesig} placeholder="All designations" options={[...new Set(acts.items.map((a) => a.designation).filter(Boolean))].sort()} />
          <PillTabs testid="actions-filter" value={af} onChange={setAf} tabs={[["open", "Open"], ["mine", "Mine"], ["team", "My team"], ["overdue", "Overdue"], ["done", "Done"]]} /></div>}>
          <ActionsBoard items={items} filter={A_FILTERS[af]} onChanged={load} onEdit={(a) => setAForm({ ...a, owner_id: team.find((t) => t.name === a.owner)?.id || "" })} />
        </Panel>
      )}
      <MeetingForm open={!!mForm} onOpenChange={(o) => !o && setMForm(null)} initial={mForm} team={team} projects={projects} onSaved={load} />
      <MomDialog meeting={mom} onOpenChange={(o) => !o && setMom(null)} onSaved={load} />
      <ActionForm open={!!aForm} onOpenChange={(o) => !o && setAForm(null)} initial={aForm} team={team} projects={projects} meetings={meetings.filter((m) => m.can_manage)} onSaved={load} />
    </>
  );
}
